import { useState } from "react"

import { Button } from "@/components/button"
import { Card } from "@/components/card"
import { Input } from "@/components/input"
import { Search } from "@/components/search"
import { MetaData } from "@/domains/common/layout/metadata"
import { PageContainer } from "@/domains/common/layout/page-container"
import { PageTitle } from "@/domains/common/layout/page-title"
import { BodyLg } from "@/domains/design/components/body-lg"
import { Heading5 } from "@/domains/design/components/heading5"
import { ScaleCard400 } from "@/domains/design/components/scale-card-400"
import { ScaleInnerCard100 } from "@/domains/design/components/scale-inner-card-100"
import { ScaleInnerCard200 } from "@/domains/design/components/scale-inner-card-200"
import { ScaleInnerCard300 } from "@/domains/design/components/scale-inner-card-300"
import { DesignSubItem } from "@/domains/navigation/subitems/design-subitem"
import { DesignSystemSubItem } from "@/domains/navigation/subitems/design-system-subitem"

export const DESIGN_BREADCRUMB_ITEMS = [new DesignSubItem(), new DesignSystemSubItem()]

type ThemeVariableInfo = {
  variable: string
  description: string
}

const ThemeLabel = ({
  variables,
  position = "top-right",
}: {
  variables: ThemeVariableInfo[]
  position?: "top-right" | "top-left" | "bottom-right" | "bottom-left"
}) => {
  if (variables.length === 0) return null

  const positionClasses = {
    "top-right": "top-2 right-2",
    "top-left": "top-2 left-2",
    "bottom-right": "bottom-2 right-2",
    "bottom-left": "bottom-2 left-2",
  }

  return (
    <div className={`absolute ${positionClasses[position]} z-10 flex flex-col gap-1`}>
      {variables.map((v) => (
        <div
          key={v.variable}
          className="rounded border border-secondary-600 bg-secondary-900/95 px-2 py-1 font-mono text-xs text-secondary-100 shadow-lg backdrop-blur-sm"
        >
          <div className="font-semibold">{v.variable}</div>
          <div className="text-xs text-secondary-400">{v.description}</div>
        </div>
      ))}
    </div>
  )
}

const InspectableComponent = ({
  children,
  variables,
  labelPosition = "top-right",
  title,
}: {
  children: React.ReactNode
  variables: ThemeVariableInfo[]
  labelPosition?: "top-right" | "top-left" | "bottom-right" | "bottom-left"
  title?: string
}) => {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <div className="relative" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      {title && <div className="mb-2 text-sm font-medium text-t200">{title}</div>}
      <div className="relative">
        {children}
        {isHovered && <ThemeLabel variables={variables} position={labelPosition} />}
      </div>
    </div>
  )
}

const DesignSystemPage = () => {
  return (
    <PageContainer>
      <MetaData title="Design | Design System" />
      <PageTitle title="Design System" subTitle="Our design system and components" items={DESIGN_BREADCRUMB_ITEMS} />

      <div className="space-y-8">
        {/* Background Color Swatches */}
        <section>
          <h2 className="mb-4 text-xl font-semibold text-t100">Background Color Variables</h2>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
            {[
              { var: "p100-light", className: "bg-p100-light" },
              { var: "p100", className: "bg-p100" },
              { var: "p100-shade", className: "bg-p100-shade" },
              { var: "p200-light", className: "bg-p200-light" },
              { var: "p200", className: "bg-p200" },
              { var: "p200-shade", className: "bg-p200-shade" },
              { var: "p300-light", className: "bg-p300-light" },
              { var: "p300", className: "bg-p300" },
              { var: "p300-shade", className: "bg-p300-shade" },
              { var: "p400-light", className: "bg-p400-light" },
              { var: "p400", className: "bg-p400" },
              { var: "p400-shade", className: "bg-p400-shade" },
              { var: "p500", className: "bg-p500" },
            ].map(({ var: varName, className }) => (
              <InspectableComponent
                key={varName}
                variables={[{ variable: varName, description: "Background" }]}
                title={varName}
              >
                <div className={`h-24 w-full rounded-lg border border-p400-light ${className} p-4`}>
                  <div className="text-xs text-t100">{varName}</div>
                </div>
              </InspectableComponent>
            ))}
          </div>
        </section>

        {/* Cards */}
        <section>
          <h2 className="mb-4 text-xl font-semibold text-t100">Cards</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <InspectableComponent
              variables={[
                { variable: "p400", description: "Background" },
                { variable: "p400-light", description: "Border" },
              ]}
              title="Card (p400)"
            >
              <Card.Container>
                <Card.Header>
                  <Card.HeaderTitle>Card Title</Card.HeaderTitle>
                  <Card.HeaderDescription>This is a card description using p400 background</Card.HeaderDescription>
                </Card.Header>
                <Card.Separator />
                <div className="text-t200">Card content goes here</div>
              </Card.Container>
            </InspectableComponent>
          </div>
        </section>

        {/* Inputs */}
        <section>
          <h2 className="mb-4 text-xl font-semibold text-t100">Inputs</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <InspectableComponent
              variables={[
                { variable: "p400", description: "Background" },
                { variable: "p400", description: "Border" },
                { variable: "p400-light", description: "Border (hover/focus)" },
              ]}
              title="Input (p400)"
            >
              <Input placeholder="Enter text..." />
            </InspectableComponent>
            <InspectableComponent
              variables={[
                { variable: "p300", description: "Background" },
                { variable: "p400", description: "Border" },
                { variable: "p200-shade", description: "Border (hover/focus)" },
              ]}
              title="Search Input (p300)"
            >
              <Search placeholder="Search..." />
            </InspectableComponent>
          </div>
        </section>

        {/* Buttons */}
        <section>
          <h2 className="mb-4 text-xl font-semibold text-t100">Buttons</h2>
          <div className="flex flex-wrap gap-4">
            <InspectableComponent
              variables={[
                { variable: "p100", description: "Background" },
                { variable: "p200-light", description: "Border" },
              ]}
              title="Primary Flat (p100)"
            >
              <Button variant="primary">Primary Flat</Button>
            </InspectableComponent>
            <InspectableComponent
              variables={[
                { variable: "p200", description: "Background" },
                { variable: "p200-light", description: "Border" },
              ]}
              title="Neutral Flat (p200)"
            >
              <Button variant="neutral">Neutral Flat</Button>
            </InspectableComponent>
            <InspectableComponent variables={[{ variable: "p300", description: "Background" }]} title="Neutral (p300)">
              <Button variant="neutral">Neutral</Button>
            </InspectableComponent>
          </div>
        </section>

        {/* Scale Cards */}
        <section>
          <h2 className="mb-4 text-xl font-semibold text-t100">Scale Cards</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <InspectableComponent
              variables={[
                { variable: "p100", description: "Background" },
                { variable: "p200-light", description: "Border" },
              ]}
              title="Scale Card 100 (p100)"
            >
              <div className="rounded-lg border border-p200-light bg-p100 p-4">Scale Card 100</div>
            </InspectableComponent>
            <InspectableComponent
              variables={[
                { variable: "p200", description: "Background" },
                { variable: "p200-light", description: "Border" },
              ]}
              title="Scale Card 200 (p200)"
            >
              <div className="rounded-lg border border-p200-light bg-p200 p-4">Scale Card 200</div>
            </InspectableComponent>
            <InspectableComponent
              variables={[
                { variable: "p300", description: "Background" },
                { variable: "p300-light", description: "Border" },
              ]}
              title="Scale Card 300 (p300)"
            >
              <div className="rounded-lg border border-p300-light bg-p300 p-4">Scale Card 300</div>
            </InspectableComponent>
            <InspectableComponent
              variables={[
                { variable: "p400", description: "Background" },
                { variable: "p400-light", description: "Border" },
              ]}
              title="Scale Card 400 (p400)"
            >
              <div className="rounded-lg border border-p400-light bg-p400 p-4">Scale Card 400</div>
            </InspectableComponent>
          </div>
        </section>

        {/* Nested Scale Cards */}
        <section>
          <h2 className="mb-4 text-xl font-semibold text-t100">Nested Scale Cards</h2>
          <div>
            <Heading5>Multi-level Nesting</Heading5>
            <ScaleCard400 className="p-6">
              <div className="space-y-4 text-t100">
                <Heading5>Level 1: ScaleCard400</Heading5>
                <ScaleInnerCard300 enabled={true} className="p-6">
                  <div className="space-y-4 text-t100">
                    <Heading5>Level 2: ScaleInnerCard300</Heading5>
                    <ScaleInnerCard200 className="p-6">
                      <div className="space-y-4 text-t100">
                        <Heading5>Level 3: ScaleInnerCard200</Heading5>
                        <ScaleInnerCard100 enabled={true} className="p-4">
                          <div className="text-t100">
                            <Heading5>Level 4: ScaleInnerCard100</Heading5>
                            <BodyLg>This is the innermost card in our hierarchy</BodyLg>
                          </div>
                        </ScaleInnerCard100>
                      </div>
                    </ScaleInnerCard200>
                  </div>
                </ScaleInnerCard300>
              </div>
            </ScaleCard400>
          </div>
        </section>
      </div>
    </PageContainer>
  )
}

export default DesignSystemPage
